import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileCirclePlus } from '@fortawesome/free-solid-svg-icons'
import { Button, Modal } from '../ui'
import PdfPages from '../pdf/PdfPages'
import LiveReadOnlyBanner from '../templates/LiveReadOnlyBanner'
import { loadStarterPreview } from './starterPreviewCache'
import { startersApiService, type Starter } from '../../services/startersApiService'
import { foldersApiService } from '../../services/foldersApiService'
import { ApiError } from '../../services/BaseApiService'
import type { Folder } from '../../types'
import { pathDepth } from '../../utils/folderTree'
import { colors, fontSize, radius, spacing } from '../../styles/theme'

interface StarterPreviewModalProps {
  starter: Starter
  readOnly: boolean
  initialFolderId?: string
  onClose: () => void
}

const fieldStyle: React.CSSProperties = {
  padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
  fontSize: fontSize.base, background: colors.surface, color: colors.text,
  boxSizing: 'border-box', width: '100%',
}

const labelStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: fontSize.sm,
  color: colors.textSecondary, flex: '1 1 200px', minWidth: 0,
}

export default function StarterPreviewModal({ starter, readOnly, initialFolderId, onClose }: StarterPreviewModalProps) {
  const navigate = useNavigate()
  const [blob, setBlob] = useState<Blob | null>(null)
  const [previewError, setPreviewError] = useState('')
  const [name, setName] = useState(starter.name)
  const [folderId, setFolderId] = useState(initialFolderId ?? '')
  const [folders, setFolders] = useState<Folder[]>([])
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    let cancelled = false
    loadStarterPreview(starter.key)
      .then(b => { if (!cancelled) setBlob(b) })
      .catch(err => { if (!cancelled) setPreviewError(err instanceof ApiError ? err.message : 'Preview unavailable.') })
    return () => { cancelled = true }
  }, [starter.key])

  useEffect(() => {
    if (readOnly) return
    let cancelled = false
    foldersApiService.listAll()
      .then(f => { if (!cancelled) setFolders(f) })
      .catch(() => { /* root is always available */ })
    return () => { cancelled = true }
  }, [readOnly])

  const folderOptions = useMemo(
    () => [...folders].sort((a, b) => a.path.localeCompare(b.path)),
    [folders],
  )

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) { setError('Name is required'); return }
    setError('')
    setCreating(true)
    try {
      const { templateId, templateVersionId } = await startersApiService.create(starter.key, name.trim(), folderId || undefined)
      navigate(`/app/templates/${templateId}/edit?versionId=${templateVersionId}`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create template')
      setCreating(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={starter.name}
      subtitle={starter.description}
      width={880}
      footer={readOnly ? <Button variant="ghost" onClick={onClose}>Close</Button> : (
        <form onSubmit={handleCreate} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: spacing.sm, width: '100%' }}>
          <label style={labelStyle}>
            Template name
            <input value={name} onChange={e => setName(e.target.value)} maxLength={200} style={fieldStyle} />
          </label>
          <label style={labelStyle}>
            Folder
            <select value={folderId} onChange={e => setFolderId(e.target.value)} style={fieldStyle}>
              <option value="">Templates (top level)</option>
              {folderOptions.map(f => (
                <option key={f.folderId} value={f.folderId}>
                  {'  '.repeat(Math.max(0, pathDepth(f.path) - 1)) + f.name}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" variant="primary" loading={creating} icon={<FontAwesomeIcon icon={faFileCirclePlus} />}>
            Use this layout
          </Button>
          {error && <div role="alert" style={{ color: colors.error, fontSize: fontSize.sm, flexBasis: '100%' }}>{error}</div>}
        </form>
      )}
    >
      {readOnly && <LiveReadOnlyBanner />}
      {previewError
        ? <div style={{ color: colors.error, fontSize: fontSize.sm }}>{previewError}</div>
        : blob
          ? <PdfPages file={blob} maxWidth={760} onError={setPreviewError} />
          : <div style={{ color: colors.textSecondary, fontSize: fontSize.sm, padding: spacing.lg, textAlign: 'center' }}>Loading preview…</div>}
    </Modal>
  )
}
